import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import StatCard from '../../components/ui/StatCard';
import GradientIcon from '../../components/ui/GradientIcon';
import { Users, Star, Briefcase, BookOpen } from 'lucide-react';
import Modal from '../../components/ui/Modal';
import Pagination from '../../components/ui/Pagination';
import { userService } from '../../services';
import exportService from '../../services/exportService';

/**
 * Users Management Page (Super Admin)
 * Manage all users across all organizations
 */

const UsersPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orgFilter = searchParams.get('org');
  const orgNameParam = searchParams.get('orgName');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState(orgFilter ? 'trainer' : 'all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [orgName, setOrgName] = useState(orgNameParam || '');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, user: null });
  const [bulkDeleteModal, setBulkDeleteModal] = useState({ isOpen: false });
  const [bulkStatusModal, setBulkStatusModal] = useState({ isOpen: false, status: '' });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    clients: 0,
    trainers: 0,
  });

  useEffect(() => {
    fetchUsers();
  }, [filterRole, filterStatus, orgFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        limit: 100,
      };
      if (filterRole !== 'all') params.role = filterRole;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (searchTerm) params.search = searchTerm;
      if (orgFilter) params.organization = orgFilter;

      const response = await userService.getUsers(params);

      // Helper to format role name
      const formatRole = (role) => {
        if (role === 'super-admin') return 'Super Admin';
        return role.charAt(0).toUpperCase() + role.slice(1);
      };

      // Helper to format date nicely
      const formatDate = (dateString, includeTime = false) => {
        if (!dateString) return 'Never';
        const date = new Date(dateString);
        const options = {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          ...(includeTime && { hour: '2-digit', minute: '2-digit' })
        };
        return date.toLocaleDateString('en-US', options);
      };

      // Transform API data to match frontend format
      const transformedUsers = response.data.map(user => ({
        id: user._id,
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        role: formatRole(user.role),
        organization: user.organization?.name || 'N/A',
        organizationId: user.organization?._id,
        status: user.status,
        lastLogin: formatDate(user.lastLogin, true),
        joinedDate: formatDate(user.createdAt),
        sessionsCreated: 0,
      }));

      setUsers(transformedUsers);
      // Fallback only used when backend doesn't return stats. individualTrainers
      // can't be computed from the trimmed user list (no org type here), so we
      // ship 0 — backend's stats.individualTrainers is the source of truth.
      setStats(response.stats || {
        total: transformedUsers.length,
        individualTrainers: 0,
        clients: transformedUsers.filter(u => u.role.toLowerCase() === 'client').length,
        trainers: transformedUsers.filter(u => u.role.toLowerCase() === 'trainer').length,
      });
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = () => {
    fetchUsers();
  };

  // Handle export
  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportService.exportUsersCSV();
      toast.success('Users exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Failed to export users');
    } finally {
      setIsExporting(false);
    }
  };

  // Filter users locally for instant feedback
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      searchTerm === '' ||
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.organization.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterRole, filterStatus]);

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'Client':
        return 'primary';
      case 'Trainer':
        return 'success';
      case 'Admin':
        return 'error';
      case 'Super Admin':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleSelectUser = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map((u) => u.id));
    }
  };

  const handleDeleteClick = (user) => {
    setDeleteModal({ isOpen: true, user });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.user) return;

    setIsDeleting(true);
    try {
      await userService.deleteUser(deleteModal.user.id);

      // Remove from local state
      setUsers((prev) => prev.filter((user) => user.id !== deleteModal.user.id));
      setSelectedUsers((prev) => prev.filter((id) => id !== deleteModal.user.id));

      toast.success(`${deleteModal.user.name} has been deleted`);
      setDeleteModal({ isOpen: false, user: null });
    } catch (error) {
      toast.error('Failed to delete user');
      console.error('Error deleting user:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, user: null });
  };

  // Bulk delete handlers
  const handleBulkDeleteClick = () => {
    setBulkDeleteModal({ isOpen: true });
  };

  const handleBulkDeleteConfirm = async () => {
    setIsBulkDeleting(true);
    try {
      // Delete each selected user
      const deletePromises = selectedUsers.map(userId => userService.deleteUser(userId));
      await Promise.all(deletePromises);

      // Remove from local state
      setUsers((prev) => prev.filter((user) => !selectedUsers.includes(user.id)));

      toast.success(`${selectedUsers.length} user(s) deleted successfully`);
      setSelectedUsers([]);
      setBulkDeleteModal({ isOpen: false });
    } catch (error) {
      toast.error('Failed to delete some users');
      console.error('Error bulk deleting users:', error);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleBulkDeleteCancel = () => {
    setBulkDeleteModal({ isOpen: false });
  };

  // Bulk status change handlers
  const handleBulkStatusChange = (status) => {
    setBulkStatusModal({ isOpen: true, status });
  };

  const handleBulkStatusConfirm = async () => {
    setIsBulkUpdating(true);
    try {
      await userService.bulkUpdateStatus(selectedUsers, bulkStatusModal.status);

      // Update local state
      setUsers((prev) =>
        prev.map((user) =>
          selectedUsers.includes(user.id)
            ? { ...user, status: bulkStatusModal.status }
            : user
        )
      );

      toast.success(`${selectedUsers.length} user(s) status updated to ${bulkStatusModal.status}`);
      setSelectedUsers([]);
      setBulkStatusModal({ isOpen: false, status: '' });
    } catch (error) {
      toast.error('Failed to update user status');
      console.error('Error bulk updating status:', error);
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleBulkStatusCancel = () => {
    setBulkStatusModal({ isOpen: false, status: '' });
  };

  return (
    <DashboardLayout title={orgFilter && orgName ? `${orgName} - Trainers` : 'Users'}>
      <div className="p-4 space-y-4">
        {/* Org filter banner */}
        {orgFilter && (
          <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
            <span className="text-sm text-amber-800">
              Showing trainers for: <strong>{orgName || 'Organization'}</strong>
            </span>
            <button
              onClick={() => navigate('/admin/users')}
              className="text-sm font-medium text-amber-700 hover:text-amber-900 underline"
            >
              Show All Users
            </button>
          </div>
        )}

        {/* Header */}
        <div className="mb-3">
          <button
            onClick={() => orgFilter ? navigate(`/admin/organizations/${orgFilter}`) : navigate('/admin/dashboard')}
            className="inline-flex items-center gap-1.5 text-neutral-600 hover:text-neutral-900 transition-colors mb-3 group"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">{orgFilter ? 'Back to Organization' : 'Back to Dashboard'}</span>
          </button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-neutral-900">{orgFilter && orgName ? `${orgName} - Trainers` : 'Users Management'}</h1>
            <p className="text-xs text-neutral-600">{orgFilter ? `Trainers in ${orgName || 'this organization'}` : 'Manage all users across the platform'}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleExport} disabled={isExporting}>
              {isExporting ? (
                <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              )}
              <span className="hidden sm:inline ml-1.5">{isExporting ? 'Exporting...' : 'Export'}</span>
            </Button>
            <Button variant="primary" size="sm" onClick={() => navigate('/admin/users/new')}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="hidden sm:inline ml-1.5">Add User</span>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard label="Total Users"        value={stats.total}                  icon={Users}     variant="blue" />
          <StatCard label="Individual Trainers" value={stats.individualTrainers || 0} icon={Star}      variant="amber" />
          <StatCard label="Clients"            value={stats.clients}                icon={Briefcase} variant="indigo" />
          <StatCard label="Trainers"           value={stats.trainers}               icon={BookOpen}  variant="violet" />
        </div>

        {/* Bulk Actions Bar */}
        {selectedUsers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-2.5"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-blue-900">
                {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={handleBulkDeleteClick}
                >
                  <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </Button>
                <select
                  className="px-3 py-1.5 text-xs font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg shadow-sm hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all cursor-pointer appearance-none pr-8"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em' }}
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) {
                      handleBulkStatusChange(e.target.value);
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="" disabled>Change Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
                <Button variant="ghost" size="sm" onClick={() => setSelectedUsers([])}>
                  Clear Selection
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Users Table Card */}
        <div className="bg-white rounded-2xl border border-neutral-100 overflow-hidden">
          {/* Search and Filters inside card */}
          <div className="p-5 border-b border-neutral-100">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="relative flex-1 w-full sm:max-w-md">
                <svg
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by name, email, or organization..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all bg-neutral-50/50 placeholder:text-neutral-400"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="flex-1 sm:flex-none px-3 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white appearance-none pr-8 cursor-pointer text-neutral-700"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                >
                  <option value="all">All Roles</option>
                  <option value="client">Client</option>
                  <option value="trainer">Trainer</option>
                  <option value="individual_trainer">Individual Trainer</option>
                  <option value="super-admin">Super Admin</option>
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="flex-1 sm:flex-none px-3 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white appearance-none pr-8 cursor-pointer text-neutral-700"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-neutral-100">
                  <th className="text-left py-3.5 px-5 w-10">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                      onChange={handleSelectAll}
                      className="w-3.5 h-3.5 text-primary-600 rounded"
                    />
                  </th>
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">User</th>
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Role</th>
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Organization</th>
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Status</th>
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Last Login</th>
                  <th className="text-right py-3.5 px-5 text-sm font-medium text-neutral-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user) => (
                  <motion.tr
                    key={user.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border-b border-neutral-50 hover:bg-neutral-50/60 transition-colors cursor-pointer"
                    onClick={() => navigate(`/admin/users/${user.id}`)}
                  >
                    <td className="py-4 px-5" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleSelectUser(user.id)}
                        className="w-3.5 h-3.5 text-primary-600 rounded"
                      />
                    </td>
                    <td className="py-4 px-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center text-primary-600 font-semibold text-sm flex-shrink-0">
                          {user.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-neutral-900 truncate">{user.name}</p>
                          <p className="text-xs text-neutral-400 truncate">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-5">
                      <Badge variant={getRoleBadgeVariant(user.role)} size="sm">
                        {user.role}
                      </Badge>
                    </td>
                    <td className="py-4 px-5" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => navigate(`/admin/organizations/${user.organizationId}`)}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        {user.organization}
                      </button>
                    </td>
                    <td className="py-4 px-5">
                      <Badge variant={user.status === 'active' ? 'live' : 'default'} size="sm">
                        {user.status}
                      </Badge>
                    </td>
                    <td className="py-4 px-5">
                      <span className="text-sm text-neutral-500">{user.lastLogin}</span>
                    </td>
                    <td className="py-4 px-5 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-0.5">
                        <button
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                          className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
                          title="View"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                          className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteClick(user)}
                          className="p-2 rounded-lg text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>

            {paginatedUsers.length === 0 && (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-neutral-100 rounded-full mb-4">
                  <svg className="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <p className="text-sm text-neutral-600 font-medium mb-1">No users found</p>
                <p className="text-xs text-neutral-400">Try adjusting your search or filters</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredUsers.length > 0 && (
            <div className="px-3 sm:px-5 py-3 sm:py-3.5 border-t border-neutral-100">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                totalItems={filteredUsers.length}
              />
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        title="Delete User"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-neutral-700">
                Are you sure you want to delete <span className="font-semibold">{deleteModal.user?.name}</span>?
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                This action cannot be undone. The user will lose access to their account and all associated data will be permanently removed.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-neutral-200">
            <Button variant="ghost" size="sm" onClick={handleDeleteCancel} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
            >
              {isDeleting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-1.5 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Deleting...
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete User
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal
        isOpen={bulkDeleteModal.isOpen}
        onClose={handleBulkDeleteCancel}
        title="Delete Users"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-neutral-700">
                Are you sure you want to delete <span className="font-semibold">{selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''}</span>?
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                This action cannot be undone. Selected users will lose access to their accounts and all associated data will be permanently removed.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-neutral-200">
            <Button variant="ghost" size="sm" onClick={handleBulkDeleteCancel} disabled={isBulkDeleting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleBulkDeleteConfirm}
              disabled={isBulkDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
            >
              {isBulkDeleting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-1.5 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Deleting...
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete {selectedUsers.length} User{selectedUsers.length > 1 ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Status Change Confirmation Modal */}
      <Modal
        isOpen={bulkStatusModal.isOpen}
        onClose={handleBulkStatusCancel}
        title="Change User Status"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              bulkStatusModal.status === 'active' ? 'bg-green-100' :
              bulkStatusModal.status === 'inactive' ? 'bg-neutral-100' :
              'bg-orange-100'
            }`}>
              <svg className={`w-5 h-5 ${
                bulkStatusModal.status === 'active' ? 'text-green-600' :
                bulkStatusModal.status === 'inactive' ? 'text-neutral-600' :
                'text-orange-600'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {bulkStatusModal.status === 'active' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                ) : bulkStatusModal.status === 'inactive' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                )}
              </svg>
            </div>
            <div>
              <p className="text-sm text-neutral-700">
                Change status of <span className="font-semibold">{selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''}</span> to <span className="font-semibold capitalize">{bulkStatusModal.status}</span>?
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                {bulkStatusModal.status === 'active'
                  ? 'Users will be able to access their accounts and use all features.'
                  : bulkStatusModal.status === 'inactive'
                  ? 'Users will temporarily lose access to their accounts.'
                  : 'Users will be suspended and cannot log in until reactivated.'}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-neutral-200">
            <Button variant="ghost" size="sm" onClick={handleBulkStatusCancel} disabled={isBulkUpdating}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleBulkStatusConfirm}
              disabled={isBulkUpdating}
              className={
                bulkStatusModal.status === 'active' ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' :
                bulkStatusModal.status === 'inactive' ? 'bg-neutral-600 hover:bg-neutral-700 focus:ring-neutral-500' :
                'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500'
              }
            >
              {isBulkUpdating ? (
                <>
                  <svg className="animate-spin -ml-1 mr-1.5 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Updating...
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Confirm
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default UsersPage;
